import React, { ReactNode, CSSProperties, useEffect, useMemo, useRef, useState } from 'react'

type WidthVal = number | string

interface DataListProps {
    leftWidth?: WidthVal    // number -> px, string -> literal
    breakAt?: number        // px; wraps when component width <= breakAt
    indent?: number         // px indentation for description when stacked
    children: ReactNode
    className?: string
}

interface DataPairProps {
    leftWidth?: WidthVal    // per-row override
    indent?: number         // px override for this row
    children: ReactNode
    className?: string
}

interface DataTermProps { children: ReactNode, className?: string }
interface DataDescriptionProps { children: ReactNode, className?: string }

const asCss = (v: WidthVal | undefined, fallback: string) =>
    typeof v === 'number' ? `${v}px` : (v ?? fallback)

export const DataList: React.FC<DataListProps> = ({
    leftWidth,
    breakAt = 550,
    indent = 30,
    children,
    className = ''
}) => {
    const ref = useRef<HTMLDivElement | null>(null)
    const [isStacked, setIsStacked] = useState(false)

    useEffect(() => {
        const el = ref.current
        if (!el) return
        const ro = new ResizeObserver(([entry]) => {
            const w = entry.contentBoxSize?.[0]?.inlineSize ?? entry.contentRect.width
            setIsStacked(w <= breakAt)
        })
        ro.observe(el)
        return () => ro.disconnect()
    }, [breakAt])

    const style = {
        ['--dl-left-width' as any]: asCss(leftWidth, '25%'),
        ['--dl-indent' as any]: `${indent}px`
    } as CSSProperties

    return (
        <div
            ref={ref}
            className={`data-list ${isStacked ? 'is-stacked' : ''} ${className}`}
            style={style}
            data-break-at={breakAt}
        >
            {children}
        </div>
    )
}

export const DataPair: React.FC<DataPairProps> = ({
    leftWidth,
    indent,
    children,
    className = ''
}) => {
    const style: CSSProperties = {}

    if (leftWidth !== undefined) {
        style['--dl-left-width-override' as any] = asCss(leftWidth, '25%')
    }
    if (indent !== undefined) {
        style['--dl-indent-override' as any] = `${indent}px`
    }

    return (
        <div className={`data-pair ${className}`} style={style}>
            {children}
        </div>
    )
}

export const DataTerm: React.FC<DataTermProps> = ({ children, className = '' }) => {
    return (
        <div className={`data-term ${className}`}>
            {children}
        </div>
    )
}

export const DataDescription: React.FC<DataDescriptionProps> = ({ children, className = '' }) => {
    return (
        <div className={`data-description ${className}`}>
            {children}
        </div>
    )
}
